import { createTheme } from '@mui/material/styles';

// Import all font files
import EuclidCircularARegular from '../assets/fonts/Euclid Circular A Regular.ttf';
import EuclidCircularABold from '../assets/fonts/Euclid Circular A Bold.ttf';
import EuclidCircularALight from '../assets/fonts/Euclid Circular A Light.ttf';
import EuclidCircularAMedium from '../assets/fonts/Euclid Circular A Medium.ttf';
import EuclidCircularASemiBold from '../assets/fonts/Euclid Circular A SemiBold.ttf';
import EuclidCircularAItalic from '../assets/fonts/Euclid Circular A Italic.ttf';
import EuclidCircularABoldItalic from '../assets/fonts/Euclid Circular A Bold Italic.ttf';
import EuclidCircularALightItalic from '../assets/fonts/Euclid Circular A Light Italic.ttf';
import EuclidCircularAMediumItalic from '../assets/fonts/Euclid Circular A Medium Italic.ttf';
import EuclidCircularASemiBoldItalic from '../assets/fonts/Euclid Circular A SemiBold Italic.ttf';

export const colors = {
  midnightGreen: '#014953',
  pink: '#ff2386',
  bone: '#e4dac9',
  boneLight: '#f5f1eb',
  candelion: '#ace1af',
  lemonLime: '#e3ff02',
  headerBg: '#282c34',
  linkBlue: '#61dafb',
} as const;

declare module '@mui/material/styles' {
  interface Theme {
    colors: typeof colors;
  }
  interface ThemeOptions {
    colors?: typeof colors;
  }
}

const globalStyles = {
  '@global': {
    '@font-face': [
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularALight}) format('truetype')`,
        fontWeight: 300,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularALightItalic}) format('truetype')`,
        fontWeight: 300,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularARegular}) format('truetype')`,
        fontWeight: 400,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAItalic}) format('truetype')`,
        fontWeight: 400,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAMedium}) format('truetype')`,
        fontWeight: 500,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularAMediumItalic}) format('truetype')`,
        fontWeight: 500,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularASemiBold}) format('truetype')`,
        fontWeight: 600,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularASemiBoldItalic}) format('truetype')`,
        fontWeight: 600,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularABold}) format('truetype')`,
        fontWeight: 700,
        fontStyle: 'normal',
        fontDisplay: 'swap',
      },
      {
        fontFamily: 'Euclid Circular A',
        src: `url(${EuclidCircularABoldItalic}) format('truetype')`,
        fontWeight: 700,
        fontStyle: 'italic',
        fontDisplay: 'swap',
      },
    ],
  },
};

export const theme = createTheme({
  colors,
  palette: {
    primary: { main: colors.midnightGreen },
    secondary: { main: colors.pink },
    background: {
      default: colors.bone,
      paper: colors.boneLight,
    },
  },
  typography: {
    fontFamily: '"Euclid Circular A", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 500,
    },
    body1: {
      fontWeight: 400,
    },
    body2: {
      fontWeight: 300,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        ${globalStyles['@global']['@font-face']
          .map(
            font => `
          @font-face {
            font-family: ${font.fontFamily};
            src: ${font.src};
            font-weight: ${font.fontWeight};
            font-style: ${font.fontStyle};
            font-display: ${font.fontDisplay};
          }
        `
          )
          .join('\n')}

        html, body {
          margin: 0;
          padding: 0;
            height: 100%; /* Ensures the full height is used */
          font-family: "Euclid Circular A", sans-serif !important;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

/* Ensure .app fills the full viewport height */
.app {
  display: flex;
  flex-direction: column;
  min-height: 100vh; /* Ensures it spans the full viewport height */
  height: 100%; /* Required for mobile behavior */
  font-family: "Euclid Circular A", sans-serif !important;
}

/* Ensure .main-content fills the remaining space */
.main-content {
  flex: 1; /* Takes up the remaining space */
  padding: 1rem;
  background-color: ${colors.boneLight};

  @media (max-width: 768px) {
    padding: 0.5rem;
    height: 100%; /* Ensures it stretches to the parent's height */
  }

        .App {
          text-align: center;
        }

        .App-logo {
          height: 40vmin;
          pointer-events: none;
        }

        @media (prefers-reduced-motion: no-preference) {
          .App-logo {
            animation: App-logo-spin infinite 20s linear;
          }
        }

        .App-header {
          background-color: ${colors.headerBg};
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          font-size: calc(10px + 2vmin);
          color: white;
        }

        .App-link {
          color: ${colors.linkBlue};
        }

        @keyframes App-logo-spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes rotating {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .rotating {
          animation: rotating 2s linear infinite;
        }

        .markdown-content {
          font-family: inherit;
        }

        .markdown-content p {
          margin-bottom: 1em;
        }

        * {
          font-family: "Euclid Circular A", sans-serif !important;
        }
      `,
    },
  },
});
